import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, filter, map, Observable, of } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { BaseComponent } from '../../../base.component';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { ProcessingManager } from '@managers/processing.manager';
import { ProcessingStep } from '@models/ui/processing-step.model';
import { orderBy } from 'lodash-es';
import { ServerConfigService } from '@services/server-config.service';
import { rowTransitionAnimation } from "../animations";

@Component({
    selector: 'lex-all-steps',
    templateUrl: './all-steps-table.component.html',
    styleUrls: ['./all-steps-table.component.scss'],
    animations: [rowTransitionAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AllStepsTableComponent extends BaseComponent implements OnInit, OnDestroy {
	public dataSource$: Observable<MatTableDataSource<ProcessingStep>>;
	public rowHeight$ = this.processingManager.currentRowHeight$;
	public isConfigLoading = false;
	public columnsToDisplay = [
		'accountName',
		'caseName',
		'functionName',
		'functionCount'
	];

	private intervalId: any;

	constructor(
		public processingManager: ProcessingManager,
		private serverConfigService: ServerConfigService,
		private cdr: ChangeDetectorRef
	) {
		super();

		this.dataSource$ = combineLatest([
			this.processingManager.allSteps$,
			this.processingManager.currentFilterText$
		]).pipe(
			filter(([collection]) => !!collection),
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map(([logCollection, term]) => {
				if (term) {
					logCollection = logCollection?.filter((item) => {
						return item.caseName?.toLowerCase().includes(term.toLowerCase()) ||
							item.stepName?.toLowerCase().includes(term.toLowerCase()) ||
							item.accountName?.toLowerCase().includes(term.toLowerCase());
					});
				}
				logCollection = orderBy(logCollection, (item) => item.caseName.toLowerCase(), 'asc');
				return new MatTableDataSource(logCollection as any);
			})
		);
	}

	ngOnInit() {
		this.startUpdating();
	}

	ngOnDestroy() {
		clearInterval(this.intervalId);
	}

	startUpdating() {
		this.intervalId = setInterval(() => {
			this.updateTable();
		}, 1000); // Update every second
	}

	updateTable() {
		// Trigger change detection manually
		this.cdr.markForCheck();
	}

	public trackByFn(index: number, item: any) {
		return `${item.caseId}:${item.functionName}:${item.functionCount}`;
	}

	public refreshAdmissionController(): void {
		this.isConfigLoading = true;
		const configId = 'cf8ac829-02dd-ec11-aa18-121a0f91a96a';
		this.serverConfigService.getServerConfigs().pipe(
			switchMap(data => {
				const config = data.find(c => c.configId.toLowerCase() === configId);
				if (!config) return of(null);
				config.value = 'true';
				return this.serverConfigService.updateServerConfig(config);
			})
		).subscribe(() => {
			this.isConfigLoading = false;
			this.cdr.markForCheck(); // Ensure the view is updated
		});
	}
}
